'use client'
import { useMutation } from '@tanstack/react-query'
import { BookmarkButton } from '@web/app/[locale]/clubhouse/[slug]/_components/action-buttons/bookmark-button'
import { useSocialPostSaveMutation, useSocialPostUnsaveMutation } from '@web/gen/feed-post'
import * as React from 'react'
import type { Project } from '../../page-client'
import { useProject } from '../../_hooks/use-project-data'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useIsLoggedInQuery } from 'auth/hooks'
import toast from 'react-hot-toast'
import { useFeedRoute } from '../../contexts/feed-route-context'

interface Props {
  project: Project
  size?: 'small' | 'regular'
}

export const ProjectBookmarkButton = ({ size, project }: Props) => {
  const { basePath } = useFeedRoute()
  const isLoggedInQuery = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()
  const saveMutation = useSocialPostSaveMutation()
  const unsaveMutation = useSocialPostUnsaveMutation()
  const { refetch } = useProject({ postSlug: project?.postSlug || '' })

  const { mutate: toggleBookmark } = useMutation({
    // oxlint-disable-next-line require-await
    mutationFn: async (currentlySaved: boolean) => {
      return currentlySaved
        ? unsaveMutation.mutateAsync({ input: { postId: project?.id } })
        : saveMutation.mutateAsync({ input: { postId: project?.id } })
    },
    onSuccess: (_, currentlySaved) => {
      if (!currentlySaved) {
        toast.success(
          <div className="flex text-14 gap-1.5 font-semibold">
            <p>Added to your </p>
            <a className="underline" href={`${basePath}/saved`}>
              saved posts
            </a>
          </div>,
          {
            duration: 5000,
          },
        )
      }
      refetch()
    },
  })

  const handleLikeClick = React.useCallback(() => {
    if (!isLoggedInQuery.isPending && !isLoggedInQuery.data) {
      openModal('register')
      return
    }
    toggleBookmark(project?.isSaved)
  }, [project?.isSaved, toggleBookmark, isLoggedInQuery.isPending, isLoggedInQuery.data, openModal])

  return <BookmarkButton size={size} onClick={handleLikeClick} isSaved={project?.isSaved} />
}
